import { Routes } from '@angular/router';

import { AuthGuard } from './_providers/authguard';
import { LoginRedirectGuard } from './_providers/login-redirect.guard';
import { reactRoute } from './react/bridge';

export const routes: Routes = [
  { path: '', redirectTo: 'home/dashboard', pathMatch: 'full' },
  {
    path: 'account/login',
    loadComponent: () => import('./_views/account/login/login.component').then((m) => m.LoginComponent),
    canActivate: [LoginRedirectGuard],
  },
  {
    path: '',
    canActivateChild: [AuthGuard],
    children: [
      {
        path: 'account/register',
        loadComponent: () =>
          import('./_views/account/register/account-register.component').then((m) => m.AccountRegisterComponent),
        data: { role: 'UserRegistrationPending' },
      },
      {
        path: 'account/hin-linked',
        loadComponent: () =>
          import('@app/_views/account/hin-linked/account-hin-linked.component').then(
            (m) => m.AccountHinLinkedComponent,
          ),
      },
      {
        path: 'account/force-login-with-idp',
        loadComponent: () =>
          import('@app/_views/account/force-login-with-idp/force-login-with-idp.component').then(
            (m) => m.ForceLoginWithIdpComponent,
          ),
      },
      { path: 'home', redirectTo: 'home/dashboard', pathMatch: 'full' },
      {
        path: 'home/dashboard',
        loadComponent: () => import('./_views/home/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        data: { forbiddenRole: 'UserRegistrationPending' },
      },
      {
        path: 'home/info',
        loadComponent: () => import('./_views/home/info/info.component').then((m) => m.InfoComponent),
      },
      {
        path: 'home/info/edit',
        loadComponent: () => import('./_views/home/info-edit/info-edit.component').then((m) => m.InfoEditComponent),
        data: { anyRoles: ['Admin', 'InstitutionGroupAdmin'] },
      },
      {
        path: 'user/management',
        loadComponent: () =>
          import('./_views/user/user-management/user-management.component').then((m) => m.UserManagementComponent),
        data: { anyRoles: ['UserManagement', 'InstitutionGroupAdmin'] },
      },
      {
        path: 'user/management/pending',
        loadComponent: () =>
          import('./_views/user/user-management/user-management.component').then((m) => m.UserManagementComponent),
        data: { anyRoles: ['UserManagement', 'InstitutionGroupAdmin'] },
      },
      {
        path: 'user/groups',
        loadComponent: () =>
          import('./_views/user/user-groups/user-groups.component').then((m) => m.UserGroupsComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'user/profile',
        loadComponent: () =>
          import('./_views/user/user-profile/user-profile.component').then((m) => m.UserProfileComponent),
      },
      {
        path: 'user/contracts',
        loadComponent: () =>
          import('./_views/user/user-contracts/user-contracts.component').then((m) => m.UserContractsComponent),
        data: { role: 'CloseContractsUser' },
      },
      {
        path: 'user/invoices',
        loadComponent: () =>
          import('./_views/user/user-invoices/user-invoices.component').then((m) => m.UserInvoicesComponent),
        data: { role: 'Invoice' },
      },
      {
        path: 'user/invoices/:patientid',
        loadComponent: () =>
          import('./_views/user/user-invoices/user-invoices.component').then((m) => m.UserInvoicesComponent),
        data: { role: 'Invoice' },
      },
      {
        path: 'user/invoice/:id',
        loadComponent: () =>
          import('./_views/user/user-invoice/user-invoice.component').then((m) => m.UserInvoiceComponent),
        data: { role: 'Invoice' },
      },
      {
        path: 'user/invoice-create',
        loadComponent: () =>
          import('@app/_views/user/user-invoice-create/user-invoice-create.component').then(
            (m) => m.UserInvoiceCreateComponent,
          ),
        data: { role: 'Invoice' },
      },
      {
        path: 'user/invoice/visits/:id',
        loadComponent: () =>
          import('./_views/user/user-invoice-visits/user-invoice-visits.component').then(
            (m) => m.UserInvoiceVisitsComponent,
          ),
        data: { role: 'Invoice' },
      },
      {
        path: 'user/patients',
        loadComponent: () =>
          import('./_views/user/user-patients/user-patients.component').then((m) => m.UserPatientsComponent),
        data: { role: 'Invoice' },
      },
      {
        path: 'user/delegation',
        ...reactRoute(() => import('@app/react/pages').then((m) => m.DelegatePage)),
        data: { role: 'Delegate' },
      },
      {
        path: 'user/:userId/contracts',
        loadComponent: () =>
          import('./_views/user/user-contracts/user-contracts.component').then((m) => m.UserContractsComponent),
      },
      {
        path: 'invoice/controlling',
        loadComponent: () =>
          import('./_views/invoice/invoice-controlling/invoice-controlling.component').then(
            (m) => m.InvoiceControllingComponent,
          ),
        data: { role: 'InvoiceControlling' },
      },
      {
        path: 'invoice/serviceproviders',
        loadComponent: () =>
          import('./_views/invoice/invoice-serviceproviders/invoice-serviceproviders.component').then(
            (m) => m.InvoiceServiceProvidersComponent,
          ),
        data: { role: 'InvoiceControlling' },
      },
      {
        path: 'invoice/delegations',
        ...reactRoute(() => import('@app/react/pages').then((m) => m.DelegatorPage)),
        data: { role: 'Invoice' },
      },
      {
        path: 'employee/management/:groupId',
        loadComponent: () =>
          import('./_views/employee/employee-management/employee-management.component').then(
            (m) => m.EmployeeManagementComponent,
          ),
      },
      {
        path: 'insurance/contracts',
        loadComponent: () =>
          import('./_views/insurance/insurance-usercontracts/insurance-usercontracts.component').then(
            (m) => m.InsuranceUsercontractsComponent,
          ),
        data: { role: 'ViewContracts' },
      },
      {
        path: 'insurance/pendingcontracts',
        loadComponent: () =>
          import('./_views/insurance/insurance-pending-usercontracts/insurance-pending-usercontracts.component').then(
            (m) => m.InsurancePendingUsercontractsComponent,
          ),
        data: { role: 'EditContracts' },
      },
      {
        path: 'catalog/management/:catalogViewId',
        loadComponent: () =>
          import('./_views/catalog/catalog-management/catalog-management.component').then(
            (m) => m.CatalogManagementComponent,
          ),
      },
      {
        path: 'admin/eventlogs',
        loadComponent: () => import('./_views/admin/event-logs/event-logs.component').then((m) => m.EventLogsComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'admin/info',
        loadComponent: () => import('./_views/admin/admin-info/admin-info.component').then((m) => m.AdminInfoComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'admin/emails',
        loadComponent: () => import('./_views/admin/emails/emails.component').then((m) => m.EmailsComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'admin/rules',
        loadComponent: () => import('./_views/admin/rules/rules.component').then((m) => m.RulesComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'admin/insurances',
        loadComponent: () =>
          import('./_views/admin/insurances/insurances.component').then((m) => m.InsurancesComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'admin/institutions',
        loadComponent: () =>
          import('./_views/admin/institutions/institutions.component').then((m) => m.InstitutionsComponent),
        data: { role: 'Admin' },
      },
      {
        path: 'admin/contracts',
        loadComponent: () => import('./_views/admin/contracts/contracts.component').then((m) => m.ContractsComponent),
        data: { anyRoles: ['Admin', 'InstitutionGroupAdmin'] },
      },
      {
        path: 'admin/statistics',
        loadComponent: () =>
          import('./_views/statistics/statistics-container.component').then((m) => m.StatisticsContainerComponent),
        data: { role: 'Admin' },
      },
    ],
  },
  { path: '**', redirectTo: 'home/dashboard' },
];
